/**
 * Loosely-typed access to tables, columns and RPCs that are newer than the
 * generated `database.types.ts` (estimator_settings, estimates.cc_emails /
 * contact_phone / followup_days, integration_status_snapshot …).
 *
 * The generated types are regenerated deliberately, not per feature, so
 * hooks that touch these objects cast through `unknown` once here and keep
 * explicit row interfaces + explicit column lists at the call site.
 */
import { supabase } from './supabase';

export interface DbError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

export interface DbResult<T> {
  data: T | null;
  error: DbError | null;
  count?: number | null;
}

/** A PostgREST query builder that we drive without generated column types. */
export type LooseBuilder = PromiseLike<DbResult<any>> & {
  [method: string]: (...args: any[]) => LooseBuilder;
};

export const db = supabase as unknown as {
  from: (table: string) => LooseBuilder;
  rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<DbResult<any>>;
};

/** Human-readable message for a PostgREST / Postgres error. */
export function describeDbError(error: DbError | null | undefined, fallback: string): string {
  if (!error) return fallback;
  const parts = [error.message, error.details, error.hint].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' — ') : fallback;
}
